/** Spatie permission names used for UI access control. */
export type AppPermission =
  | "manage_patient"
  | "schedule_visit"
  | "visit_update"
  | "manage_layouts"
  | "manage_settings"
  | "manage_billings"
  | "manage_expenses"
  | "manage_pharmacy"
  | "manage_laboratory"
  | "view_lab_order"
  | "manage_masters"
  | "view_rooms"
  | "manage_calendar"
  | "manage_users";

const ALWAYS_ALLOWED_PREFIXES = ["/profile", "/change-password", "/reset-password"];

/** Top-level sidebar item name → required permissions (any match). */
const NAV_PERMISSIONS: Record<string, AppPermission[]> = {
  patient: ["manage_patient"],
  episodes: ["manage_patient", "schedule_visit"],
  "OPD Visits": ["manage_patient", "schedule_visit", "visit_update"],
  IPD: ["manage_patient", "schedule_visit", "visit_update"],
  bed_layout: ["view_rooms", "manage_layouts"],
  calendar: ["manage_calendar"],
  Pharmacy: ["manage_pharmacy"],
  "Lab Orders": ["manage_laboratory", "view_lab_order"],
  duty_roster: ["manage_users", "manage_settings"],
  attendance: ["manage_users", "manage_settings"],
  finance: ["manage_billings", "manage_expenses"],
  reports: ["manage_settings", "manage_billings"],
  settings: [
    "manage_settings",
    "manage_masters",
    "manage_users",
    "manage_layouts",
    "manage_pharmacy",
    "manage_laboratory",
  ],
};

/** Settings submenu item name → required permissions. */
const SETTINGS_CHILD_PERMISSIONS: Record<string, AppPermission[]> = {
  organisation: ["manage_settings"],
  masters: ["manage_masters", "manage_pharmacy", "manage_laboratory"],
  users: ["manage_users"],
  roles_and_permissions: ["manage_users"],
  layout_design: ["manage_layouts"],
};

/** Finance submenu. */
const FINANCE_CHILD_PERMISSIONS: Record<string, AppPermission[]> = {
  billing: ["manage_billings"],
  doctor_payouts: ["manage_billings"],
  expenses: ["manage_expenses"],
};

/** Path prefixes → required permissions (any). First match wins. */
const ROUTE_PERMISSIONS: Array<{ prefix: string; permissions: AppPermission[] }> = [
  { prefix: "/pharmacy", permissions: ["manage_pharmacy"] },
  { prefix: "/items-list", permissions: ["manage_pharmacy"] },
  { prefix: "/product-bundles", permissions: ["manage_pharmacy"] },
  { prefix: "/supplier-list", permissions: ["manage_pharmacy"] },
  { prefix: "/purchase-orders", permissions: ["manage_pharmacy"] },
  { prefix: "/GRNs", permissions: ["manage_pharmacy"] },
  { prefix: "/medicines", permissions: ["manage_pharmacy", "manage_masters"] },
  { prefix: "/companies", permissions: ["manage_pharmacy", "manage_masters"] },
  { prefix: "/masters", permissions: ["manage_masters", "manage_pharmacy", "manage_laboratory"] },
  { prefix: "/lab-order-list", permissions: ["manage_laboratory", "view_lab_order"] },
  { prefix: "/lab-order", permissions: ["manage_laboratory", "view_lab_order"] },
  { prefix: "/test-list", permissions: ["manage_laboratory", "manage_masters"] },
  { prefix: "/lab-categories", permissions: ["manage_laboratory", "manage_masters"] },
  { prefix: "/patients", permissions: ["manage_patient"] },
  { prefix: "/episodes", permissions: ["manage_patient", "schedule_visit"] },
  { prefix: "/opd-visits", permissions: ["manage_patient", "schedule_visit", "visit_update"] },
  { prefix: "/ipd-visits", permissions: ["manage_patient", "schedule_visit", "visit_update"] },
  { prefix: "/bed-layout", permissions: ["view_rooms", "manage_layouts"] },
  { prefix: "/calendar", permissions: ["manage_calendar"] },
  { prefix: "/billing", permissions: ["manage_billings"] },
  { prefix: "/doctor-payouts", permissions: ["manage_billings"] },
  { prefix: "/expenses", permissions: ["manage_expenses"] },
  { prefix: "/expense-categories", permissions: ["manage_masters", "manage_expenses"] },
  { prefix: "/settings", permissions: ["manage_settings"] },
  { prefix: "/users", permissions: ["manage_users"] },
  { prefix: "/roles-and-permissions", permissions: ["manage_users"] },
  { prefix: "/layout-design", permissions: ["manage_layouts"] },
  { prefix: "/duty-roster", permissions: ["manage_users", "manage_settings"] },
  { prefix: "/attendance", permissions: ["manage_users", "manage_settings"] },
  { prefix: "/dashboard", permissions: ["manage_settings", "manage_billings", "manage_patient", "schedule_visit", "visit_update"] },
  { prefix: "/reports", permissions: ["manage_settings", "manage_billings"] },
  { prefix: "/logs", permissions: ["manage_settings"] },
];

export function getUserPermissions(): string[] {
  try {
    const raw = window.localStorage.getItem("permissions");
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map((p) => String(p));
  } catch {
    return [];
  }
}

export function hasPermission(permission: string): boolean {
  return getUserPermissions().includes(permission);
}

export function hasAnyPermission(permissions: string[]): boolean {
  if (!permissions.length) return true;
  const userPerms = getUserPermissions();
  return permissions.some((p) => userPerms.includes(p));
}

/** If permissions were never stored, do not lock the UI (legacy sessions). */
export function hasPermissionData(): boolean {
  return window.localStorage.getItem("permissions") !== null;
}

export function canAccessNavItem(name: string): boolean {
  if (!hasPermissionData()) return true;
  const required = NAV_PERMISSIONS[name];
  if (!required) return true;
  return hasAnyPermission(required);
}

export function filterNavChildren(
  parentName: string,
  children: Array<{ name: string; [key: string]: any }>
): Array<{ name: string; [key: string]: any }> {
  if (!hasPermissionData()) return children;

  const map =
    parentName === "settings"
      ? SETTINGS_CHILD_PERMISSIONS
      : parentName === "finance"
        ? FINANCE_CHILD_PERMISSIONS
        : null;

  if (!map) return children;

  return children.filter((child) => {
    const required = map[child.name];
    if (!required) return true;
    return hasAnyPermission(required);
  });
}

export function canAccessPath(pathname: string): boolean {
  if (!hasPermissionData()) return true;
  if (ALWAYS_ALLOWED_PREFIXES.some((p) => pathname.startsWith(p))) return true;

  const match = ROUTE_PERMISSIONS.find((r) => pathname.startsWith(r.prefix));
  if (match) return hasAnyPermission(match.permissions);

  // Narrow roles (e.g. pharmacist with only manage_pharmacy) cannot open unlisted pages
  const perms = getUserPermissions();
  const isScopedOnly =
    perms.length > 0 &&
    perms.every((p) =>
      ["manage_pharmacy", "manage_laboratory", "view_lab_order"].includes(p)
    );
  if (isScopedOnly) return false;

  return true;
}

/** Prefer first usable area after login based on permissions. */
export function getDefaultRoute(): string {
  if (!hasPermissionData()) return "/dashboard";

  if (hasAnyPermission(["manage_patient", "schedule_visit", "visit_update", "manage_calendar", "manage_settings", "manage_billings"])) {
    return "/dashboard";
  }
  if (hasPermission("manage_pharmacy")) return "/pharmacy/pos/list";
  if (hasAnyPermission(["manage_laboratory", "view_lab_order"])) return "/lab-order-list";
  if (hasPermission("manage_masters")) return "/masters";
  if (hasPermission("manage_users")) return "/users";
  if (hasPermission("manage_settings")) return "/settings";

  return "/dashboard";
}

export function canViewMasterSection(
  section: "clinical" | "administrative" | "pathological" | "pharmacy" | "insurance" | "ot" | "vaccination"
): boolean {
  if (!hasPermissionData()) return true;

  switch (section) {
    case "pharmacy":
      return hasAnyPermission(["manage_pharmacy", "manage_masters"]);
    case "pathological":
      return hasAnyPermission(["manage_laboratory", "manage_masters"]);
    default:
      return hasPermission("manage_masters");
  }
}
